import LinkOffIcon from '@mui/icons-material/LinkOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import { useCallback, useMemo, useState } from 'react';
import NRReactGrid from '../../../NRReactGrid/NRReactGrid';
import { DateProvider } from '../../../NRReactGridCustomComponents/NRCustomProviders/DateProvider';
import { NRMenu } from '../../../NRMenu/NRMenu';
import { CONSTANTS } from '../../../../constants/constants';
import { VULNERABILITY_JIRA_TICKETS_LABELS } from './vulnerabilityJiraTickets.constants';
import { NRJiraTicketIdProvider } from './providers/NRJiraTicketIdProvider';
import { NRJiraTicketStatusProvider } from './providers/NRJiraTicketStatusProvider';

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  '& .MuiSvgIcon-root': {
    fontSize: 24,
    color: theme.palette.text.icon,
    marginRight: theme.spacing(1.5)
  }
}));

const COLUMNS = [
  {
    title: { value: VULNERABILITY_JIRA_TICKETS_LABELS.TICKET_ID },
    name: 'ticketKey',
    sortDisabled: true,
    hideFilter: true
  },
  {
    title: { value: VULNERABILITY_JIRA_TICKETS_LABELS.STATUS },
    name: 'status',
    sortDisabled: true,
    hideFilter: true
  },
  {
    title: { value: VULNERABILITY_JIRA_TICKETS_LABELS.ASSIGNEE },
    name: 'assignee',
    sortDisabled: true,
    hideFilter: true
  },
  {
    title: { value: VULNERABILITY_JIRA_TICKETS_LABELS.UPDATED },
    name: 'updatedAt',
    sortDisabled: true,
    hideFilter: true,
    dateFormat: CONSTANTS.DATE_FORMAT.MONTH_YEAR_SHORT
  }
];

const COLUMN_EXTENSIONS = [
  { columnName: 'ticketKey', align: 'left', width: 180 },
  { columnName: 'status', align: 'left', width: 120 },
  { columnName: 'assignee', align: 'left', width: 120 },
  { columnName: 'updatedAt', align: 'left', width: 90 }
];

function NRJiraTicketsTable({ tickets = [], onUnlink }) {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const handleActionClick = useCallback((event, _gridActions, row) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedTicket(row);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedTicket(null);
  }, []);

  const handleUnlinkClick = useCallback(() => {
    if (selectedTicket) {
      onUnlink?.(selectedTicket);
    }
    handleCloseMenu();
  }, [handleCloseMenu, onUnlink, selectedTicket]);

  const rows = useMemo(
    () =>
      tickets.map(ticket => ({
        ...ticket,
        actions: [
          {
            title: VULNERABILITY_JIRA_TICKETS_LABELS.TICKET_ACTIONS,
            icon: (
              <IconButton
                size="small"
                aria-label={`${VULNERABILITY_JIRA_TICKETS_LABELS.TICKET_ACTIONS} for ${ticket.ticketKey}`}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            ),
            action: handleActionClick,
            customStyles: {
              backgroundColor: 'unset'
            }
          }
        ]
      })),
    [handleActionClick, tickets]
  );

  return (
    <Box
      sx={{
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        '& .MuiPaper-root': {
          backgroundColor: 'transparent'
        }
      }}
    >
      <NRReactGrid
        rows={rows}
        columns={COLUMNS}
        columnExtensions={COLUMN_EXTENSIONS}
        embedded
        hasExtraActions
        showIntegratedSelection={false}
        showSelectAll={false}
        showFilterSelector={false}
        addDefaultSorting={false}
        statesArray={[]}
        renderersArray={[]}
        providersArray={[
          <NRJiraTicketIdProvider key="nrJiraTicketIdProvider" for={['ticketKey']} />,
          <NRJiraTicketStatusProvider key="nrJiraTicketStatusProvider" for={['status']} />,
          <DateProvider key="jiraTicketUpdatedAtProvider" for={['updatedAt']} />
        ]}
        showIntegratedPagination={false}
        allowColumnReOrdering={false}
        elevation={0}
        variant="elevation"
      />

      <NRMenu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
        <StyledMenuItem onClick={handleUnlinkClick}>
          <LinkOffIcon />
          {VULNERABILITY_JIRA_TICKETS_LABELS.UNLINK}
        </StyledMenuItem>
      </NRMenu>
    </Box>
  );
}

export default NRJiraTicketsTable;
